import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
import GlobalSearchState from 'flarum/forum/states/GlobalSearchState';
import FieldSet from 'flarum/common/components/FieldSet';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Component from 'flarum/common/Component';
import setRouteWithForcedRefresh from 'flarum/common/utils/setRouteWithForcedRefresh';
import CommunityHero from './components/CommunityHero';
import CommunityJoin from './components/CommunityJoin';
import CommunitySetup from './components/CommunitySetup';
import CommunityMembers from './components/CommunityMembers';
import CommunityGovernors from './components/CommunityGovernors';
import CommunityKingdoms from './components/CommunityKingdoms';
import { ensurePlacesNav } from './placesNav';
import { fillSide, clearSideCache } from './components/CommunitySide';
import JoinPage from './components/JoinPage';
import { currentInvite } from '../common/invite';
import { ROUTE, MEMBERS_ROUTE, GOVERNORS_ROUTE, KINGDOMS_ROUTE, onKingdoms, data, setData, onCommunity, onMembers, onGovernors, param, feedTags, isEditing, activePlace } from '../common/community';
import { ensureRankingAbout } from './components/RankingAbout';
import EventsList from './events/EventsList';
import EventView from './events/EventView';
import EventForm from './events/EventForm';
import TagEvents from './events/TagEvents';
import registerEventNotifications from './events/notifications';
import { EVENTS_ROUTE, EVENT_ROUTE, NEW_EVENT_ROUTE, onEvents, onNewEvent, onEvent, onAnyEvents } from './events/util';
import BusinessList from './businesses/BusinessList';
import BusinessView from './businesses/BusinessView';
import BusinessForm from './businesses/BusinessForm';
import BusinessReview from './businesses/BusinessReview';
import BusinessJoin from './businesses/BusinessJoin';
import registerSearch from './search';
import registerNotifications from './notifications';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import { linkNewDiscussion } from './businesses/util';
import ProjectList from './projects/ProjectList';
import ProjectView from './projects/ProjectView';
import ProjectForm from './projects/ProjectForm';
import ProjectJoin from './projects/ProjectJoin';
import ProjectFollow from './projects/ProjectFollow';
import TagProjects from './projects/TagProjects';
import * as proj from './projects/util';
import { JOIN_ROUTE, onJoin as onBusinessJoin, LIST_ROUTE, NEW_ROUTE, REVIEW_ROUTE, PAGE_ROUTE, onNew as onNewBusiness, onReview as onBusinessReview, onPage as onBusinessPage, onAnyBusinesses, editing as editingBusiness } from './businesses/util';

/** The banner on the events pages */
function eventsHero() {
  return m("header", {
    className: "Hero VouchaCommunityHero VEvt-hero"
  }, m("div", {
    className: "container"
  }, m("div", {
    className: "containerNarrow"
  }, m("h1", {
    className: "Hero-title"
  }, m("i", {
    className: "far fa-calendar",
    "aria-hidden": "true"
  }), " ", onNewEvent() ? 'Post an event' : 'Events'), m("div", {
    className: "Hero-subtitle"
  }, "Meetups, talks and gatherings, in person and online."))));
}

/** The banner on the business directory's pages */
function businessesHero() {
  return m("header", {
    className: "Hero VouchaCommunityHero VBiz-hero-banner"
  }, m("div", {
    className: "container"
  }, m("div", {
    className: "containerNarrow"
  }, m("h1", {
    className: "Hero-title"
  }, m("i", {
    className: "fas fa-store",
    "aria-hidden": "true"
  }), " ", onNewBusiness() ? 'Add your business' : 'Businesses'), m("div", {
    className: "Hero-subtitle"
  }, "Find, vouch for and work with businesses run by Voucha members."))));
}

/** The banner on the projects pages (not on a project's own page) */
function projectsHero() {
  return m("header", {
    className: "Hero VouchaCommunityHero VBiz-hero-banner"
  }, m("div", {
    className: "container"
  }, m("div", {
    className: "containerNarrow"
  }, m("h1", {
    className: "Hero-title"
  }, m("i", {
    className: "fas fa-people-carry-box",
    "aria-hidden": "true"
  }), " ", proj.onNew() ? 'Start a project' : proj.communityOpen() ? 'Community projects' : 'Projects'), m("div", {
    className: "Hero-subtitle"
  }, proj.communityOpen() ? 'Projects members run in Nigeria’s states: follow them, vouch for them, join in.' : 'Plan your own: a house, land, a business. Community projects are coming soon.'))));
}

/** Setup (first visit) or edit: loads the member's saved places, then shows the stepper. */
class SetupLoader extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.location = undefined;
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/voucha-community/me',
      errorHandler: () => {}
    }).then(r => {
      this.location = r.location;
      this.profile = r.profile;
    }).catch(() => this.location = null).finally(() => m.redraw());
  }
  view() {
    if (this.location === undefined) return m(LoadingIndicator, null);
    return m(CommunitySetup, {
      location: this.location,
      profile: this.profile,
      start: param('edit'),
      invite: currentInvite(),
      onfinish: async () => {
        const overview = await app.request({
          method: 'GET',
          url: app.forum.attribute('apiUrl') + '/voucha-community/overview',
          errorHandler: () => {}
        });
        setData(Object.assign({}, data(), {
          state: 'ok',
          places: overview.places || []
        }));
        clearSideCache();
        setRouteWithForcedRefresh(app.route(ROUTE));
      }
    });
  }
}

// ---------- the "Your community" sidebar section ----------
let sideTimer = null;
function scheduleSide(tries = 0) {
  clearTimeout(sideTimer);
  sideTimer = setTimeout(() => ensureSide(tries), 80);
}
function ensureSide(tries) {
  ensureRankingAbout(); // governors page: "About this ranking" at the top of the right sidebar
  const navReady = ensurePlacesNav();
  const existing = document.querySelector('.VouchaSide-slot');
  if (!navReady && tries < 25) scheduleSide(tries + 1);
  if (!onCommunity() || data().state !== 'ok' || isEditing()) {
    if (existing) {
      m.render(existing, null);
      existing.remove();
    }
    return;
  }
  const aside = document.querySelector('.Voucha-rightSidebar');
  if (!aside || !navReady) {
    if (tries < 25) scheduleSide(tries + 1); // the theme builds its sidebars just after the page
    if (!aside) return;
  }
  if (existing && existing.parentNode === aside) return;
  if (existing) existing.remove();
  const slot = document.createElement('div');
  slot.className = 'VouchaSide-slot';
  aside.insertBefore(slot, aside.firstChild);
  fillSide(slot);
}

// ---------- friendly empty states for quiet places ----------
const LOCATION_PARENTS = ['states'];

/** The place name to show in an empty state, or null if this page isn't a place. */
function emptyPlaceName() {
  if (onCommunity()) {
    if (data().state !== 'ok' || isEditing()) return null;
    const p = activePlace();
    return p ? p.label : 'your community';
  }
  const slug = param('tags');
  if (!slug) return null;
  const tag = app.store.all('tags').find(t => t.slug() === slug);
  if (!tag) return null;
  const cities = data().cities || {};
  const placeSlugs = Object.values(cities).flatMap(c => [c.tag, ...(c.cities || []).map(x => x.slug)]);
  const parent = tag.parent && tag.parent();
  const isState = parent && LOCATION_PARENTS.includes(parent.slug());
  return placeSlugs.includes(slug) || isState ? tag.name() : null;
}
function emptyCard(place, onCommunityPage) {
  const start = () => {
    const button = document.querySelector('.IndexPage-newDiscussion');
    if (button) button.click();
  };
  return m("div", {
    className: "VouchaEmpty"
  }, m("i", {
    className: "fas fa-seedling",
    "aria-hidden": "true"
  }), m("strong", null, "No discussions in ", place, " yet"), m("p", null, "Be the first to start a conversation", onCommunityPage ? ' here' : ' for Nigerians in ' + place, ": ask a question, share local news, or introduce yourself."), m("div", {
    className: "VouchaEmpty-actions"
  }, m("button", {
    className: "Button Button--primary",
    onclick: start
  }, "Start a discussion"), app.session.user && data().state === 'ok' ? m("a", {
    className: "Button",
    href: app.route(MEMBERS_ROUTE)
  }, "See who's near you") : null));
}

// Profile line cache: user id -> { living, home, origin }
const profileCache = {};
app.initializers.add('voucha-community', () => {
  registerSearch();
  registerNotifications();
  // My Community is a real index page: same sidebars and feed as a tag page
  app.routes[ROUTE] = {
    path: '/community',
    component: IndexPage
  };
  app.routes[MEMBERS_ROUTE] = {
    path: '/community/members',
    component: IndexPage
  };
  app.routes[GOVERNORS_ROUTE] = {
    path: '/community/governors',
    component: IndexPage
  };
  app.routes[KINGDOMS_ROUTE] = {
    path: '/community/kingdoms',
    component: IndexPage
  };
  app.routes['voucha-community.join'] = {
    path: '/join/:code',
    component: JoinPage
  };
  // Events (the form's route before the event page's, so /events/new isn't read as an event)
  app.routes[EVENTS_ROUTE] = {
    path: '/events',
    component: IndexPage
  };
  app.routes[NEW_EVENT_ROUTE] = {
    path: '/events/new',
    component: IndexPage
  };
  app.routes[EVENT_ROUTE] = {
    path: '/events/:id',
    component: IndexPage
  };
  // Businesses (new and review before the business page, so they aren't read as slugs)
  app.routes[LIST_ROUTE] = {
    path: '/businesses',
    component: IndexPage
  };
  app.routes[NEW_ROUTE] = {
    path: '/businesses/new',
    component: IndexPage
  };
  app.routes[REVIEW_ROUTE] = {
    path: '/businesses/review',
    component: IndexPage
  };
  app.routes[JOIN_ROUTE] = {
    path: '/businesses/join/:token',
    component: IndexPage
  };
  app.routes[PAGE_ROUTE] = {
    path: '/businesses/:slug',
    component: IndexPage
  };
  // Community projects (new, join and follow links before the project page)
  app.routes[proj.LIST_ROUTE] = {
    path: '/projects',
    component: IndexPage
  };
  app.routes[proj.NEW_ROUTE] = {
    path: '/projects/new',
    component: IndexPage
  };
  app.routes[proj.JOIN_ROUTE] = {
    path: '/projects/join/:token',
    component: IndexPage
  };
  app.routes[proj.FOLLOW_ROUTE] = {
    path: '/projects/:follow/:token',
    component: IndexPage
  };
  app.routes[proj.PAGE_ROUTE] = {
    path: '/projects/:slug',
    component: IndexPage
  };
  extend(DiscussionPage.prototype, 'show', function (r, discussion) {
    if (discussion) {
      linkNewDiscussion(discussion);
      proj.linkNewProjectDiscussion(discussion);
    }
  });
  try {
    registerEventNotifications();
  } catch (e) {
    console.error("voucha events notifications", e);
  }

  // Feed: discussions from the member's places (all of them, or the chosen tab)
  extend(GlobalSearchState.prototype, 'params', function (params) {
    if (!onCommunity() || data().state !== 'ok') return;
    const tags = feedTags();
    if (tags.length) {
      params.filter = Object.assign({}, params.filter, {
        tag: tags.join(',')
      });
    }
  });
  // Keep the chosen place when changing sort
  extend(GlobalSearchState.prototype, 'stickyParams', function (params) {
    if (onCommunity() && param('place')) params.place = param('place');
  });
  override(IndexPage.prototype, 'hero', function (original) {
    if (onAnyEvents()) return eventsHero();
    if (onAnyBusinesses()) return onBusinessPage() && !editingBusiness() ? null : businessesHero();
    if (proj.onAnyProjects()) return proj.onPage() && !proj.editing() ? null : projectsHero();
    return onCommunity() || onMembers() || onGovernors() || onKingdoms() ? m(CommunityHero, null) : original();
  });

  // Phones: the header names the page (Flarum shows "All Discussions" on every list-based page)
  extend(IndexPage.prototype, 'oninit', function () {
    const label = onAnyEvents() ? 'Events' : onAnyBusinesses() ? 'Businesses' : proj.onAnyProjects() ? 'Projects' : onKingdoms() ? 'Kingdoms' : onGovernors() ? 'Governors' : onMembers() ? 'Members' : onCommunity() ? 'Discussions' : null;
    if (label && app.current) {
      app.current.set('titleControlLabel', label);
      app.current.set('vouchaTitleLabel', label); // read by the theme: wins over the active nav item's name
    }
  });
  extend(IndexPage.prototype, 'contentItems', function (items) {
    if (onAnyBusinesses()) {
      items.remove('toolbar');
      items.remove('discussionList');
      const slug = m.route.param('slug');
      const view = onBusinessJoin() ? m(BusinessJoin, {
        key: m.route.param('token')
      }) : onNewBusiness() ? m(BusinessForm, null) : onBusinessReview() ? m(BusinessReview, null) : onBusinessPage() ? editingBusiness() ? m(BusinessForm, {
        key: 'edit-' + slug,
        slug: slug
      }) : m(BusinessView, {
        key: slug
      }) : m(BusinessList, null);
      items.add('vouchaBusinesses', m("div", {
        className: "VouchaCommunity-panel"
      }, view), 100);
      return;
    }
    if (proj.onAnyProjects()) {
      items.remove('toolbar');
      items.remove('discussionList');
      const slug = m.route.param('slug');
      const view = proj.onJoin() ? m(ProjectJoin, {
        key: m.route.param('token')
      }) : proj.onFollow() ? m(ProjectFollow, {
        key: m.route.param('token')
      }) : proj.onNew() ? m(ProjectForm, null) : proj.onPage() ? proj.editing() ? m(ProjectForm, {
        key: 'edit-' + slug,
        slug: slug
      }) : m(ProjectView, {
        key: slug
      }) : m(ProjectList, null);
      items.add('vouchaProjects', m("div", {
        className: "VouchaCommunity-panel"
      }, view), 100);
      return;
    }
    if (onAnyEvents()) {
      items.remove('toolbar');
      items.remove('discussionList');
      const view = onNewEvent() ? m(EventForm, null) : onEvent() ? m(EventView, {
        key: m.route.param('id')
      }) : m(EventsList, null);
      items.add('vouchaEvents', m("div", {
        className: "VouchaCommunity-panel"
      }, view), 100);
      return;
    }
    // a place's tag page: its next events (hidden when there are none)
    const tagSlug = param('tags');
    if (tagSlug && tagSlug.indexOf(',') === -1) items.add('vouchaTagEvents', m(TagEvents, {
      slug: tagSlug
    }), 116);
    // a state's tag page: its community projects (hidden for other tags and when there are none)
    if (tagSlug && tagSlug.indexOf(',') === -1) items.add('vouchaTagProjects', m(TagProjects, {
      slug: tagSlug
    }), 115);
    if (onKingdoms()) {
      items.remove('toolbar');
      items.remove('discussionList');
      items.add('vouchaKingdoms', m("div", {
        className: "VouchaCommunity-panel"
      }, m(CommunityKingdoms, null)), 100);
      return;
    }
    if (onGovernors()) {
      items.remove('toolbar');
      items.remove('discussionList');
      items.add('vouchaGovernors', m("div", {
        className: "VouchaCommunity-panel"
      }, m(CommunityGovernors, null)), 100);
      return;
    }
    if (onMembers()) {
      items.remove('toolbar');
      items.remove('discussionList');
      items.add('vouchaMembers', m("div", {
        className: "VouchaCommunity-panel"
      }, m(CommunityMembers, null)), 100);
      return;
    }
    // Friendly empty state for quiet places (My Community tabs, and location tag pages)
    const place = emptyPlaceName();
    if (place && app.discussions && app.discussions.isEmpty()) {
      items.add('vouchaEmpty', emptyCard(place, onCommunity()), 95);
    }
    if (!onCommunity()) return;
    const state = data().state;
    if (state === 'guest' || state === 'setup' || isEditing()) {
      items.remove('toolbar');
      items.remove('discussionList');
      items.add('vouchaCommunity', m("div", {
        className: "VouchaCommunity-panel"
      }, state === 'guest' ? m(CommunityJoin, null) : m(SetupLoader, null)), 100);
    }
  });
  extend(IndexPage.prototype, 'oncreate', () => {
    scheduleSide();
    // Signed up through an invite and not set up yet: go to My Community (once per visit)
    try {
      if (app.session.user && data().state === 'setup' && currentInvite() && !onCommunity() && !sessionStorage.getItem('vouchaInviteSent')) {
        sessionStorage.setItem('vouchaInviteSent', '1');
        setTimeout(() => m.route.set(app.route(ROUTE)), 0);
      }
    } catch (e) {}
  });
  extend(IndexPage.prototype, 'onupdate', () => scheduleSide());

  // Settings: where you've set your places, and a link to change them
  extend('flarum/forum/components/SettingsPage', 'settingsItems', function (items) {
    if (!app.session.user) return;
    const d = data();
    const work = d.profile || {};
    items.add('vouchaLocation', m(FieldSet, {
      className: "Settings-vouchaLocation",
      label: "Your community"
    }, d.state === 'ok' && d.places.length ? m("ul", {
      className: "VouchaSettings-places"
    }, d.places.map(p => m("li", null, m("i", {
      className: p.icon,
      "aria-hidden": "true"
    }), " ", p.full)), work.profession ? m("li", null, m("i", {
      className: "fas fa-briefcase",
      "aria-hidden": "true"
    }), " ", work.profession) : null, work.study ? m("li", null, m("i", {
      className: "fas fa-graduation-cap",
      "aria-hidden": "true"
    }), " ", work.study) : null) : m("p", {
      className: "helpText"
    }, "You haven't set up your community yet."), m("div", {
      className: "VouchaSettings-actions"
    }, m(Button, {
      className: "Button",
      icon: "fas fa-map-marker-alt",
      onclick: () => m.route.set(app.route(ROUTE, d.state === 'ok' ? {
        edit: 1
      } : {}))
    }, d.state === 'ok' ? 'Change your places' : 'Set up My Community'), d.state === 'ok' ? m(Button, {
      className: "Button",
      icon: "fas fa-briefcase",
      onclick: () => m.route.set(app.route(ROUTE, {
        edit: 'profession'
      }))
    }, work.profession || work.study ? 'Change your profession' : 'Add your profession') : null)), -5);
  });

  // Profiles: where they live, their home in Nigeria and their origin (only what they show)
  extend('flarum/forum/components/UserCard', 'infoItems', function (items) {
    const user = this.attrs.user;
    if (!user) return;
    const id = user.id();
    const cached = profileCache[id];
    if (cached === undefined) {
      profileCache[id] = null;
      app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/voucha-community/users/' + id,
        errorHandler: () => {}
      }).then(r => profileCache[id] = r).catch(() => profileCache[id] = {}).finally(() => m.redraw());
      return;
    }
    if (!cached) return;
    if (cached.living) items.add('vouchaLiving', m("span", {
      className: "VouchaProfilePlace"
    }, m("i", {
      className: "fas fa-map-marker-alt"
    }), " ", cached.living), 6);
    if (cached.home) items.add('vouchaHome', m("span", {
      className: "VouchaProfilePlace"
    }, m("i", {
      className: "fas fa-home"
    }), " ", cached.home), 5);
    if (cached.origin) items.add('vouchaOrigin', m("span", {
      className: "VouchaProfilePlace"
    }, m("i", {
      className: "fas fa-seedling"
    }), " From ", cached.origin), 4);
    if (cached.profession) items.add('vouchaProfession', m("span", {
      className: "VouchaProfilePlace"
    }, m("i", {
      className: "fas fa-briefcase"
    }), " ", cached.profession), 3);
    if (cached.study) items.add('vouchaStudy', m("span", {
      className: "VouchaProfilePlace"
    }, m("i", {
      className: "fas fa-graduation-cap"
    }), " ", cached.study), 2);
  });
});